

import jsPDF from "jspdf";
import QRCode from "qrcode";
import autoTable from "jspdf-autotable";

export const generateBillPDF = async (labelData) => {
    if (!labelData || !labelData.SalePrint) return;

    // ------------------ Dynamic height for long bills ------------------
    const baseHeight = 230;
    const itemCount = labelData.SalePrintItems?.length || 0;
    const dynamicHeight = Math.max(baseHeight, 120 + itemCount * 8 + 80); // estimate

    const doc = new jsPDF({
        orientation: "portrait",
        unit: "mm",
        format: [71, dynamicHeight], // auto-extend for more items
    });

    let y = 10;

    // ------------------ Number to Words ------------------
    const NumberToWords = (num) => {
        const units = ["", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine"];
        const teens = ["Eleven", "Twelve", "Thirteen", "Fourteen", "Fifteen", "Sixteen", "Seventeen", "Eighteen", "Nineteen"];
        const tens = ["", "Ten", "Twenty", "Thirty", "Forty", "Fifty", "Sixty", "Seventy", "Eighty", "Ninety"];

        const convert = (n) => {
            if (n < 10) return units[n];
            if (n < 20 && n > 10) return teens[n - 11];
            if (n < 100) return tens[Math.floor(n / 10)] + (n % 10 ? " " + units[n % 10] : "");
            if (n < 1000) return units[Math.floor(n / 100)] + " Hundred" + (n % 100 ? " " + convert(n % 100) : "");
            if (n < 100000) return convert(Math.floor(n / 1000)) + " Thousand" + (n % 1000 ? " " + convert(n % 1000) : "");
            if (n < 10000000) return convert(Math.floor(n / 100000)) + " Lakh" + (n % 100000 ? " " + convert(n % 100000) : "");
            return "Number too large";
        };

        if (num === 0) return "Zero";
        return convert(num);
    };

    const amountInWords = (amount) => {
        const formatted = parseFloat(amount).toFixed(2);
        const [rupees, paise] = formatted.split(".");
        let words = `Rupees ${NumberToWords(parseInt(rupees))}`;
        if (paise !== "00") words += ` and ${NumberToWords(parseInt(paise))} Paise`;
        return `${words} Only`;
    };

    // ------------------ Company Header ------------------
    doc.setFontSize(15);
    doc.setFont("helvetica", "bold");
    doc.text(labelData.SalePrint.CompanyName || "", 36, y, { align: "center" });
    y += 7;

    doc.setFontSize(8);
    doc.setFont("helvetica", "normal");
    if (labelData.SalePrint.CompanyAddress) {
        doc.text(labelData.SalePrint.CompanyAddress, 36, y, { align: "center", maxWidth: 68 });
        y += 8;
    }
    doc.setFontSize(9);
    doc.setFont("helvetica", "normal");
    if (labelData.SalePrint.CompanyContactNo) {
        doc.text(`Ph: ${labelData.SalePrint.CompanyContactNo}`, 36, y, { align: "center" });
        y += 4;
    }
    doc.setFont("helvetica", "bold");
    if (labelData.SalePrint.BillRemarks) {
        doc.text(`fssai Number: ${labelData.SalePrint.BillRemarks}`, 36, y, { align: "center" });
        y += 4;
    }

    if (labelData.SalePrint.GSTNumber) {
        doc.text(`GST Number: ${labelData.SalePrint.GSTNumber}`, 36, y, { align: "center" });
        y += 4;
    }

    y += 2;
    doc.setFontSize(12);
    doc.setFont("helvetica", "bold");
    doc.text("SALE BILL", 36, y, { align: "center" });
    y += 7;

    // ------------------ Bill Info ------------------
    doc.setFontSize(9);
    doc.setFont("helvetica", "bold");

    doc.text(`Table: ${labelData.SalePrint.TableNmae || ""}`, 5, y, { align: "left" });
    doc.text(`Bill No: ${labelData.SalePrint.InvoiceNumber}`, 67, y, { align: "right" });
    y += 4;

    const billDate = new Date(labelData.SalePrint.InvoiceDate).toLocaleDateString("en-GB");
    const billTime = new Date(`2000-01-01T${labelData.SalePrint.InvoiceTime}`).toLocaleTimeString("en-US", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
    });

    doc.text(`Date: ${billDate}`, 5, y, { align: "left" });
    doc.text(`Time: ${billTime}`, 67, y, { align: "right" });
    y += 5;

    // ------------------ Item Table ------------------
    const pageWidth = doc.internal.pageSize.getWidth();
    const marginX = 1.5;
    const usableWidth = pageWidth - marginX * 2;

    const tableColumns = ["Particular", "Qty", "Rate", "Amount"];
    const tableRows = labelData.SalePrintItems.map((item) => [
        item.ItemName || "",
        (item.Quantity || 0),
        (item.Rate || 0).toFixed(2),
        (item.Amount || 0).toFixed(2),
    ]);

    const totalQty = labelData.SalePrintItems.reduce((sum, i) => sum + (i.Quantity || 0), 0);
    const totalAmount = labelData.SalePrintItems.reduce((sum, i) => sum + (i.Amount || 0), 0);

    tableRows.push([
        { content: "Total", colSpan: 1, styles: { halign: "right", fontStyle: "bold" } },
        { content: totalQty, styles: { halign: "center", fontStyle: "bold" } },
        { content: "", styles: { halign: "right" } },
        { content: totalAmount.toFixed(2), styles: { halign: "right", fontStyle: "bold" } },
    ]);

    autoTable(doc, {
        startY: y,
        head: [tableColumns],
        body: tableRows,
        margin: { left: 1 }, // << reduced left margin here
        theme: "grid",
        styles: { fontSize: 9, lineColor: [0, 0, 0], lineWidth: 0.2 },
        headStyles: { fillColor: [255, 255, 255], textColor: 0, fontStyle: "bold", fontSize: 8, halign: "center" },
        bodyStyles: {
            textColor: [0, 0, 0],       // 🔥 ensure body text stays black
        },
        columnStyles: {
            0: { halign: "left", cellWidth: usableWidth * 0.4 },
            1: { halign: "center", cellWidth: usableWidth * 0.12 },
            2: { halign: "right", cellWidth: usableWidth * 0.2 },
            3: { halign: "right", cellWidth: usableWidth * 0.23 },
        },
        margin: { left: marginX, right: marginX },
        didDrawPage: (data) => (y = data.cursor.y),
    });

    // ------------------ Totals ------------------
    y += 5;
    const totals = [];
    if (labelData.SalePrint.DiscountAmount > 0) totals.push(["Discount", labelData.SalePrint.DiscountAmount]);
    if (labelData.SalePrint.CGSTAmount > 0) totals.push(["CGST", labelData.SalePrint.CGSTAmount]);
    if (labelData.SalePrint.SGSTAmount > 0) totals.push(["SGST", labelData.SalePrint.SGSTAmount]);
    if (labelData.SalePrint.PackingCharge > 0) totals.push(["Packing", labelData.SalePrint.PackingCharge]);
    totals.push(["Round Off", labelData.SalePrint.RoundOff]);
    totals.push(["Grand Total", labelData.SalePrint.TotalAmount]);

    doc.setFontSize(9);
    totals.forEach(([label, value]) => {
        // Add top margin for Grand Total
        if (label === "Grand Total") {
            y += 2; // Add extra spacing before Grand Total
            doc.setFont("helvetica", "bold");
            doc.setFontSize(12);
            // Adjust positions for Grand Total to prevent collision
            doc.text(label, 45, y, { align: "right" });
            doc.text((Number(value) || 0).toFixed(2), 66, y, { align: "right" });
        } else {
            doc.setFont("helvetica", "bold");
            doc.setFontSize(9);
            doc.text(label, 50, y, { align: "right" });
            doc.text((Number(value) || 0).toFixed(2), 65, y, { align: "right" });
        }
        
        y += 4;
    });

    // Reset font to normal after totals
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    
    doc.line(5, y, 70, y);
    y += 4;

    // ------------------ Amount in Words ------------------
    doc.setFontSize(8);
    doc.text(amountInWords(labelData.SalePrint.TotalAmount), 36, y, {
        align: "center",
        maxWidth: 66,
    });
    y += 6;

    // ------------------ QR Code ------------------
    if (labelData.SalePrint.paymentqr) {
        try {
            const qrDataUrl = await QRCode.toDataURL(labelData.SalePrint.paymentqr);
            // Label above QR
            doc.setFont("helvetica", "bold");
            doc.setFontSize(9);
            doc.text("Scan QR for Payment", 36, y, { align: "center" });
            y += 4;
            doc.addImage(qrDataUrl, "PNG", 21, y, 30, 30);
            y += 35;
        } catch (err) {
            console.error("QR generation failed:", err);
        }
    }

    // ------------------ Description ------------------
    if (labelData.SalePrint.Description) {
        doc.setFont("helvetica", "normal");
        doc.setFontSize(8);
        doc.text(`Note: ${labelData.SalePrint.Description}`, 36, y, { align: "center", maxWidth: 66 });
        y += 6;
    }

    // ------------------ Footer ------------------
    doc.setFontSize(9);
    doc.text("THANK YOU. VISIT AGAIN!", 36, y, { align: "center" });
    y += 5;

    doc.setFont("helvetica", "italic");
    doc.setFontSize(8);
    doc.text("Powered by Efftabs Solutions LLP", 36, y, { align: "center" });

    // ------------------ Print ------------------
    const blob = doc.output("blob");
    const blobUrl = URL.createObjectURL(blob);
    const win = window.open(blobUrl, "_blank");
    if (win) {
        win.onload = () => setTimeout(() => win.print(), 500);
    }
};
